import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SlowLife = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Lifestyle</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              Slow Life – The Philosophy That Changes Everyday Life
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>June 7, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>10 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share Article
              </Button>
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/5a51808f-6165-41aa-8676-40e235829648.png" 
              alt="Person relaxing in nature with coffee, embodying slow life philosophy"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic">
              Imagine starting your day without rushing. You wake up, prepare your coffee, and instead of scrolling through notifications, you take a moment to simply sit by the window. The world outside moves slowly: sunlight rising, the sound of footsteps on the street, your breath finding its natural rhythm. This is not laziness. This is the essence of *slow life*.
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Where did slow life come from?</h2>

              <p>
                The slow life movement originated in Italy in the 1980s, when journalist Carlo Petrini protested against the opening of a fast-food chain in Rome's historic center. What began as a defense of local cuisine – <em>slow food</em> – evolved into a philosophy of life. Today, slow life is a conscious response to the acceleration of modern life, offering balance instead of constant busyness.
              </p>

              <p>
                Research shows that chronic stress and lack of rest increase the risk of burnout, depression, and cardiovascular disease (World Health Organization, 2020). By contrast, slowing down helps regulate our nervous system, improves relationships, and increases life satisfaction (Kabat-Zinn, 2013).
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">What does slow life mean in practice?</h2>

              <p>
                Slow life is not about abandoning ambition or moving to a cottage in the woods. It is about <strong className="text-accent">intentional choices that create more space for presence and meaning</strong>. Examples include:
              </p>

              <ul className="space-y-3 ml-6">
                <li><strong className="text-accent">Eating meals slowly</strong>, without screens, noticing taste and texture.</li>
                <li><strong className="text-accent">Planning fewer tasks</strong> in a day to allow deeper focus on what matters.</li>
                <li><strong className="text-accent">Walking instead of driving</strong> short distances.</li>
                <li><strong className="text-accent">Making space for unhurried conversations</strong> with friends and family.</li>
              </ul>

              <p>
                These practices align with findings in psychology: people who cultivate everyday mindfulness report <strong className="text-accent">lower stress and higher well-being</strong> (Brown & Ryan, 2003).
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Slow life in the city and in nature</h2>

              <p>
                One of the misconceptions is that slow life is possible only in idyllic, rural landscapes. In fact, it can be practiced anywhere. Even in big cities, you can create <strong className="text-accent">"islands of calm"</strong>: a daily walk in the park, a weekend without obligations, or a family meal with phones put away. The key is awareness and intention.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Why does it matter now?</h2>

              <p>
                We live in an era where being busy has become a badge of honor. Yet evidence suggests that constant busyness undermines productivity and creativity (Kreutzer et al., 2019). <strong className="text-accent">Slow life does not mean doing less – it means doing what matters most, with presence</strong>. It is both a philosophy and a form of resistance against the cult of speed.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Conclusion</h2>

              <p>
                Slow life is an invitation to reclaim time, presence, and connection. It is a reminder that life is not about how fast we move, but about how deeply we experience it. We may not control the pace of the world, but we can choose our own rhythm.
              </p>

              <p className="text-xl font-light italic text-accent">
                In a world that celebrates speed, choosing slowness becomes an act of quiet rebellion.
              </p>

              <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>• World Health Organization. (2020). Mental health and stress-related disorders.</p>
                  <p>• Kabat-Zinn, J. (2013). <em>Full Catastrophe Living: Using the Wisdom of Your Body and Mind to Face Stress, Pain, and Illness</em>.</p>
                  <p>• Brown, K. W., & Ryan, R. M. (2003). The benefits of being present: Mindfulness and its role in psychological well-being. <em>Journal of Personality and Social Psychology, 84</em>(4), 822–848.</p>
                  <p>• Kreutzer, R., Neugebauer, T., & Pattloch, A. (2019). <em>Digital Business Leadership</em>. Springer.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default SlowLife;